// src/pages/purchaseOrder/PurchaseOrderDetailsPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Package,
  Edit,
  CheckCircle,
  XCircle,
  Truck,
  Calendar,
  DollarSign,
  FileText,
  User,
  AlertCircle,
  ShoppingCart,
  Building2,
  Phone,
  Mail,
  MapPin,
  Sheet,
  PackagePlus,
  Undo2,
  Printer,
  ExternalLink,
  History,
  ChevronDown,
  ChevronUp,
  Clock,
  RefreshCw,
  Activity,
  CreditCard,
  Loader2
} from 'lucide-react';
import ProductThumb from '../procurement/components/ProductThumb';
import {
  getPurchaseOrderById,
  cancelPurchaseOrder,
  getPurchaseOrderAuditHistory,
  getPurchaseOrderAuditSummary
} from '../../services/api/purchaseOrder';
import { getGoodsReceivedNotes, cancelGoodsReceivedNote, receiveEntirePurchaseOrder } from '../../services/api/procurement/grn';
import { getReturnsToVendor } from '../../services/api/procurement/returnToVendor';
import { getDebitNotes } from '../../services/api/procurement/debitNote';
import { getPaymentsForPurchaseOrder } from '../../services/api/procurement/supplierPayment';
import { exportPurchaseOrderExcel } from '../../utils/purchaseOrderExport';
import { exportPurchaseOrderPDF } from '../../utils/procurementPdfExporter';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import { formatCurrency } from '../../utils/orderCalculations';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { getImageUrl } from '../../utils/imageUtils';
import { getCssVar } from '../../utils/themeColors';
import { buildProcurementCreatePath, buildReturnState } from '../../utils/procurementNavigation';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import PrintPreviewModal from '../../components/common/PrintPreviewModal/PrintPreviewModal';
import PaymentHistoryList from '../../components/common/PaymentHistoryList/PaymentHistoryList';
import PurchaseOrderA4Document from './PurchaseOrderA4Document';
import ps from '../procurement/components/ProcurementDetails.module.css';
import styles from './PurchaseOrderDetailsPage.module.css';

const AUDIT_MONEY_FIELDS = ['amount', 'grandTotal', 'totalAmount', 'extraCharges', 'discountAmount', 'totalDebitAmount', 'unitPrice', 'lineTotal'];
const AUDIT_ITEM_FIELDS = ['quantity', 'unitPrice', 'lineTotal', 'notes'];
const AUDIT_ITEM_GROUPS = [
  { key: 'updated', labelKey: 'purchaseOrder.auditItemUpdated' },
  { key: 'added', labelKey: 'purchaseOrder.auditItemAdded' },
  { key: 'removed', labelKey: 'purchaseOrder.auditItemRemoved' },
];
const AUDIT_ENTITY_ROUTES = {
  grn: (id) => `/procurement/grn/${id}`,
  rtv: (id) => `/procurement/return-to-vendor/${id}`,
  debit_note: (id) => `/procurement/debit-notes/${id}`,
};
const AUDIT_ENTITY_TAG_KEYS = {
  supplier_payment: 'purchaseOrder.auditTag_payment',
  grn: 'purchaseOrder.auditTag_grn',
  rtv: 'purchaseOrder.auditTag_rtv',
  debit_note: 'purchaseOrder.auditTag_debitNote',
};
const AUDIT_HIDDEN_FIELDS = ['supplierId', 'warehouseId', 'updatedFields', 'id'];
const AUDIT_LIMIT = 10;
const PAYMENT_RECORD_AUDIT_FIELDS = [
  { key: 'paymentMethodLabel', field: 'method' },
  { key: 'paymentAmount', field: 'amount' },
  { key: 'reference', field: 'reference' },
  { key: 'paymentDate', field: 'paymentDate' },
  { key: 'proofUrl', field: 'proofUrl' },
  { key: 'debitNoteNumber', field: 'debitNoteNumber' },
];

const getAuditActionIcon = (action) => {
  if (action === 'CREATE' || action.endsWith('_CREATED') || action.endsWith('_ADDED') || action.endsWith('_ISSUED') || action === 'RECEIVE_FULL') {
    return <CheckCircle size={14} />;
  }
  if (action === 'CANCEL' || action.endsWith('_CANCELLED') || action.endsWith('_REMOVED') || action.endsWith('_VOIDED')) {
    return <XCircle size={14} />;
  }
  if (action === 'UPDATE' || action.endsWith('_UPDATED')) return <Edit size={14} />;
  if (action.endsWith('_APPLIED') || action === 'STATUS_CHANGED') return <RefreshCw size={14} />;
  return <Activity size={14} />;
};

const getAuditActionColor = (action) => {
  if (action === 'CREATE' || action.endsWith('_CREATED') || action.endsWith('_ADDED') || action.endsWith('_ISSUED') || action === 'RECEIVE_FULL') {
    return getCssVar('--color-success-dark');
  }
  if (action === 'CANCEL' || action.endsWith('_CANCELLED') || action.endsWith('_REMOVED') || action.endsWith('_VOIDED')) {
    return getCssVar('--color-error');
  }
  if (action === 'UPDATE' || action.endsWith('_UPDATED')) return getCssVar('--color-info');
  if (action === 'STATUS_CHANGED') return getCssVar('--color-primary');
  if (action.endsWith('_APPLIED')) return getCssVar('--color-warning');
  return getCssVar('--color-text-secondary');
};

const Badge = ({ type, label }) => (
  <span className={`${styles.badge} ${styles[`badge_${type}`]}`}>{label}</span>
);

const statusConfig = {
  draft: { label: null, color: '#6b7280', bgColor: '#f3f4f6' },
  pending: { label: null, color: '#f59e0b', bgColor: '#fef3c7' },
  received: { label: null, color: '#10b981', bgColor: '#d1fae5' },
  partial: { label: null, color: '#3b82f6', bgColor: '#dbeafe' },
  cancelled: { label: null, color: '#ef4444', bgColor: '#fee2e2' }
};

const PurchaseOrderDetailsPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();
  const { hasPermission } = usePermissions();

  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [isReceiving, setIsReceiving] = useState(false);
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [showPrint, setShowPrint] = useState(false);

  // Related documents (§9.5 step 6) — PurchaseOrder has no backend hasMany association to any of
  // these, so each is fetched separately, filtered by purchaseOrderId (same workaround pattern
  // already used by GoodsReceivedNoteDetailsPage's secondary PO fetch).
  const [goodsReceivedNotes, setGoodsReceivedNotes] = useState([]);
  const [returnsToVendor, setReturnsToVendor] = useState([]);
  const [debitNotes, setDebitNotes] = useState([]);
  const [payments, setPayments] = useState([]);

  // Audit history (PurchaseOrderAudits): summary on mount, entries lazily on expand
  const [showAuditHistory, setShowAuditHistory] = useState(false);
  const [auditHistory, setAuditHistory] = useState([]);
  const [auditSummary, setAuditSummary] = useState(null);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [isLoadingAudit, setIsLoadingAudit] = useState(false);

  // Per-GRN cancel modal (replaces the old whole-order Reverse action)
  const [cancelGrnTarget, setCancelGrnTarget] = useState(null);
  const [grnCancellationReason, setGrnCancellationReason] = useState('');
  const [isCancellingGrn, setIsCancellingGrn] = useState(false);

  useEffect(() => {
    loadPurchaseOrder();
  }, [orderId]);

  const loadPurchaseOrder = async () => {
    try {
      setIsLoading(true);
      const response = await getPurchaseOrderById(orderId);
      setPurchaseOrder(response.purchaseOrder);
      loadRelatedDocuments();
      loadAuditSummary();
      if (showAuditHistory) loadAuditHistory(1);
    } catch (err) {
      console.error('Failed to load purchase order:', err);
      showError(err.response?.data?.message || t('purchaseOrder.failedToLoadOrder'), {
        title: t('common.error'),
        duration: 6000
      });
      navigate('/inventory/purchase-orders');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setShowAuditHistory(false);
    setAuditHistory([]);
  }, [orderId]);

  useEffect(() => {
    if (showAuditHistory && !auditHistory.length) {
      loadAuditHistory();
    }
  }, [showAuditHistory]);

  const loadAuditSummary = async () => {
    try {
      const response = await getPurchaseOrderAuditSummary(orderId);
      setAuditSummary(response.summary);
    } catch (err) {
      console.error('Failed to load purchase order audit summary:', err);
    }
  };

  const loadAuditHistory = async (page = 1) => {
    try {
      setIsLoadingAudit(true);
      const result = await getPurchaseOrderAuditHistory(orderId, { page, limit: AUDIT_LIMIT });
      setAuditHistory(result.audits || []);
      setAuditTotal(result.total || 0);
      setAuditPage(page);
    } catch (err) {
      console.error('Failed to load purchase order audit history:', err);
    } finally {
      setIsLoadingAudit(false);
    }
  };

  const formatAuditValue = (field, value) => {
    if (value === null || value === undefined || value === '') return t('purchaseOrder.auditEmpty');
    if (typeof value === 'boolean') return value ? t('common.yes') : t('common.no');
    if (AUDIT_MONEY_FIELDS.includes(field)) return formatCurrency(value);
    if (field === 'proofUrl') {
      return (
        <a href={getImageUrl(value)} target="_blank" rel="noopener noreferrer" className={styles.auditDocLink}>
          {t('purchaseOrder.auditViewFile')}
        </a>
      );
    }
    if (field === 'status') {
      return t(`purchaseOrder.status${String(value).charAt(0).toUpperCase()}${String(value).slice(1)}`);
    }
    if (field === 'orderDate' || field === 'expectedDeliveryDate' || field === 'paymentDate') return formatDate(value);
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const renderChangeRows = (rows) => {
    if (rows.length === 0) return null;
    return (
      <div className={styles.auditChanges}>
        {rows.map(({ field, from, to }) => (
          <div key={field} className={styles.auditChange}>
            <span className={styles.changeField}>{t(`purchaseOrder.auditField_${field}`)}</span>
            <div className={styles.changeValues}>
              <span className={styles.oldValue}>{formatAuditValue(field, from)}</span>
              <span className={styles.changeArrow}>→</span>
              <span className={styles.newValue}>{formatAuditValue(field, to)}</span>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderPaymentRecordChange = ({ from, to }) => renderChangeRows(
    PAYMENT_RECORD_AUDIT_FIELDS
      .map(({ key, field }) => ({ field, from: from?.[key] ?? null, to: to?.[key] ?? null }))
      .filter((row) => row.from !== row.to)
  );

  // `items` is `{ added, removed, updated }` per product; older entries stored only `{ to: count }`
  const renderItemChanges = (items) => {
    if (!items || typeof items !== 'object') return null;
    if (!items.added && !items.removed && !items.updated) {
      return (
        <div className={styles.auditChanges}>
          <div className={styles.auditChange}>
            <span className={styles.changeField}>{t('purchaseOrder.auditField_items')}</span>
            <span>{t('purchaseOrder.auditLegacyItems', { count: items.to ?? '' })}</span>
          </div>
        </div>
      );
    }
    return AUDIT_ITEM_GROUPS.flatMap(({ key, labelKey }) => (items[key] || []).map((item, index) => {
      const rows = key === 'updated'
        ? Object.entries(item.changes || {}).map(([field, value]) => ({ field, from: value.from ?? null, to: value.to ?? null }))
        : AUDIT_ITEM_FIELDS
          .filter((field) => item[field] !== null && item[field] !== undefined && item[field] !== '')
          .map((field) => (key === 'added' ? { field, from: null, to: item[field] } : { field, from: item[field], to: null }));
      return (
        <div key={`${key}-${index}`} className={styles.auditItemChange}>
          <div className={styles.auditItemHeader}>
            <strong>{item.productName || t('common.na')}</strong>
            {item.sku && <span className={styles.auditItemSku}>{item.sku}</span>}
            <span className={styles.auditPaymentRecordTag}>{t(labelKey)}</span>
          </div>
          {renderChangeRows(rows)}
        </div>
      );
    }));
  };

  // Handles ecom-shaped `created`, `{field:{from,to}}` diffs, and legacy flat snapshots (shown as Empty → value)
  const renderAuditChanges = (changes = {}) => {
    if (changes.paymentRecord) return renderPaymentRecordChange(changes.paymentRecord);
    const source = changes.created?.to || changes;
    return (
      <>
        {renderChangeRows(
          Object.entries(source)
            .filter(([field]) => !AUDIT_HIDDEN_FIELDS.includes(field) && field !== 'items')
            .map(([field, value]) => (
              value && typeof value === 'object' && ('from' in value || 'to' in value)
                ? { field, from: value.from ?? null, to: value.to ?? null }
                : { field, from: null, to: value }
            ))
        )}
        {renderItemChanges(changes.items || source.items)}
      </>
    );
  };

  const loadRelatedDocuments = async () => {
    try {
      const [grnRes, rtvRes, debitNoteRes, paymentRes] = await Promise.all([
        getGoodsReceivedNotes({ purchaseOrderId: orderId }),
        getReturnsToVendor({ purchaseOrderId: orderId }),
        getDebitNotes({ purchaseOrderId: orderId }),
        getPaymentsForPurchaseOrder(orderId),
      ]);
      setGoodsReceivedNotes(grnRes.goodsReceivedNotes || []);
      setReturnsToVendor(rtvRes.returnsToVendor || []);
      setDebitNotes(debitNoteRes.debitNotes || []);
      setPayments(paymentRes.supplierPayments || paymentRes.payments || []);
    } catch (err) {
      console.error('Failed to load related procurement documents:', err);
    }
  };

  const canEdit = purchaseOrder && !['received', 'cancelled'].includes(purchaseOrder.status) && !purchaseOrder.hasActiveReceipts && hasPermission('edit_purchase_order');
  const canReceive = purchaseOrder && ['pending', 'partial'].includes(purchaseOrder.status) && hasPermission('receive_purchase_order');
  const canCancel = purchaseOrder
    && !['received', 'partial', 'cancelled'].includes(purchaseOrder.status)
    && !purchaseOrder.hasActiveReceipts
    && !(purchaseOrder.supplierPayments || []).length
    && hasPermission('cancel_purchase_order');
  const canCreateGrn = purchaseOrder && ['pending', 'partial'].includes(purchaseOrder.status) && hasPermission('create_grn');
  const canCreateRtv = purchaseOrder && ['received', 'partial'].includes(purchaseOrder.status) && hasPermission('create_rtv');
  const canCancelGrn = ['received', 'partial'].includes(purchaseOrder?.status) && hasPermission('cancel_grn');

  const handleCancel = async () => {
    try {
      setIsCancelling(true);
      await cancelPurchaseOrder(orderId, { cancellationReason: cancellationReason || null });
      success(t('purchaseOrder.purchaseOrderCancelled', { orderNumber: purchaseOrder.purchaseOrderNumber }), {
        title: t('common.success'),
        duration: 4000
      });
      setShowCancelDialog(false);
      setCancellationReason('');
      loadPurchaseOrder();
    } catch (err) {
      console.error('Failed to cancel purchase order:', err);
      showError(err.response?.data?.message || t('purchaseOrder.failedToCancel'), {
        title: t('common.error'),
        duration: 6000
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const handleReceive = async () => {
    try {
      setIsReceiving(true);
      await receiveEntirePurchaseOrder(orderId);
      success(t('purchaseOrder.purchaseOrderReceived', { orderNumber: purchaseOrder.purchaseOrderNumber }), {
        title: t('common.success'),
        duration: 4000
      });
      setShowReceiveDialog(false);
      loadPurchaseOrder();
    } catch (err) {
      console.error('Failed to receive purchase order:', err);
      showError(err.response?.data?.message || t('purchaseOrder.failedToReceive'), {
        title: t('common.error'),
        duration: 6000
      });
    } finally {
      setIsReceiving(false);
    }
  };

  const handleCancelGrn = async () => {
    if (!cancelGrnTarget) return;
    try {
      setIsCancellingGrn(true);
      await cancelGoodsReceivedNote(cancelGrnTarget.id, { cancellationReason: grnCancellationReason.trim() || undefined });
      success(t('goodsReceivedNote.goodsReceivedNoteCancelledSuccessfully'), { title: t('common.success'), duration: 4000 });
      setCancelGrnTarget(null);
      setGrnCancellationReason('');
      loadPurchaseOrder();
    } catch (err) {
      console.error('Failed to cancel GRN:', err);
      showError(err.response?.data?.message || t('goodsReceivedNote.failedToCancelGoodsReceivedNote'), {
        title: t('common.error'),
        duration: 6000
      });
    } finally {
      setIsCancellingGrn(false);
    }
  };

  const handleDownloadExcel = async () => {
    if (!purchaseOrder || isDownloadingExcel) return;
    try {
      setIsDownloadingExcel(true);
      await exportPurchaseOrderExcel(purchaseOrder);
    } catch (err) {
      console.error('Failed to generate Excel:', err);
      showError(t('purchaseOrder.failedToLoadData'));
    } finally {
      setIsDownloadingExcel(false);
    }
  };

  const getItemVariationLabel = (item) => {
    if (!item.variationId || !item.ProductVariation) return null;
    const attrs = item.ProductVariation.attributeValues;
    if (attrs && attrs.length > 0) {
      return attrs.map(a => `${a.ProductAttribute?.name}: ${a.value}`).join(', ');
    }
    return item.ProductVariation.sku || null;
  };

  const getReceivedStatus = (item) => {
    const ordered = parseFloat(item.quantity);
    const received = parseFloat(item.receivedQuantity);
    if (received >= ordered) return 'full';
    if (received > 0) return 'partial';
    return 'none';
  };

  const getStatusInfo = (status) => {
    const cfg = statusConfig[status] || statusConfig.draft;
    const labelKey = `purchaseOrder.status${status.charAt(0).toUpperCase() + status.slice(1)}`;
    return { ...cfg, label: t(labelKey) };
  };

  if (isLoading) {
    return (
      <div>
        <PageHeader
          title={t('purchaseOrder.purchaseOrderDetails')}
          description={t('purchaseOrder.purchaseOrderDetailsDescription')}
          icon={Package}
          actions={
            <button onClick={() => navigate('/inventory/purchase-orders')} className={styles.backButton}>
              <ArrowLeft size={20} />
              {t('purchaseOrder.backToPurchaseOrders')}
            </button>
          }
        />
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinnerLarge}></div>
          <h3>{t('purchaseOrder.loadingOrder')}</h3>
          <p>{t('purchaseOrder.loadingOrderMessage')}</p>
        </div>
      </div>
    );
  }

  if (!purchaseOrder) return null;

  const statusInfo = getStatusInfo(purchaseOrder.status);
  const detailReturnState = buildReturnState(`/inventory/purchase-orders/${orderId}`, purchaseOrder.purchaseOrderNumber);

  return (
    <div>
      <PageHeader
        title={purchaseOrder.purchaseOrderNumber}
        description={t('purchaseOrder.purchaseOrderDetailsDescription')}
        icon={Package}
        className={styles.purchaseOrderHeader}
        actions={
          <div className={styles.pageActions}>
            <button onClick={() => navigate('/inventory/purchase-orders')} className={styles.backButton}>
              <ArrowLeft size={20} />
              {t('purchaseOrder.backToPurchaseOrders')}
            </button>

            {canEdit && (
              <Link
                to={`/inventory/purchase-orders/${orderId}/edit`}
                className={styles.editButton}
              >
                <Edit size={16} />
                <span>{t('common.edit')}</span>
              </Link>
            )}

            {canReceive && (
              <button
                onClick={() => setShowReceiveDialog(true)}
                className={`${ps.toneButton} ${ps.toneSuccess}`}
                disabled={isReceiving}
              >
                <CheckCircle size={16} />
                <span>{t('purchaseOrder.receiveOrder')}</span>
              </button>
            )}

            {canCreateGrn && (
              <Link
                to={buildProcurementCreatePath('/procurement/grn/create', { purchaseOrderId: orderId }, `/inventory/purchase-orders/${orderId}`, purchaseOrder.purchaseOrderNumber)}
                state={detailReturnState}
                className={`${ps.toneButton} ${ps.toneSuccess}`}
              >
                <PackagePlus size={16} />
                <span>{t('purchaseOrder.createGoodsReceivedNote')}</span>
              </Link>
            )}

            {canCreateRtv && (
              <Link
                to={buildProcurementCreatePath('/procurement/return-to-vendor/create', { purchaseOrderId: orderId }, `/inventory/purchase-orders/${orderId}`, purchaseOrder.purchaseOrderNumber)}
                state={detailReturnState}
                className={`${ps.toneButton} ${ps.toneWarning}`}
              >
                <Undo2 size={16} />
                <span>{t('purchaseOrder.createReturnToVendor')}</span>
              </Link>
            )}

            {canCancel && (
              <button
                onClick={() => setShowCancelDialog(true)}
                className={styles.cancelButton}
                disabled={isCancelling}
              >
                <XCircle size={16} />
                <span>{t('common.cancel')}</span>
              </button>
            )}

            <div className={styles.downloadGroup}>
              <button
                onClick={() => setShowPrint(true)}
                className={styles.downloadPdfButton}
                title={t('purchaseOrder.downloadPdf')}
              >
                <Printer size={16} />
                <span>{t('common.print')}</span>
              </button>
              <button
                onClick={handleDownloadExcel}
                className={styles.downloadExcelButton}
                disabled={isDownloadingExcel}
                title={t('purchaseOrder.downloadExcel')}
              >
                <Sheet size={16} />
                <span>{isDownloadingExcel ? t('purchaseOrder.downloading') : t('common.excel')}</span>
              </button>
            </div>
          </div>
        }
      />

      <div className={styles.mainContent}>
        {/* Left Column */}
        <div className={styles.leftColumn}>
          {/* Order Information */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <FileText className={styles.cardIcon} size={20} />
              <h3>{t('purchaseOrder.orderInformation')}</h3>
            </div>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{t('purchaseOrder.poNumber')}</span>
                <span className={styles.infoValue}>{purchaseOrder.purchaseOrderNumber}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{t('purchaseOrder.status')}</span>
                <span
                  className={styles.statusBadge}
                  style={{ backgroundColor: statusInfo.bgColor, borderColor: statusInfo.color, color: statusInfo.color }}
                >
                  {statusInfo.label}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{t('purchaseOrder.orderDate')}</span>
                <span className={styles.infoValue}>
                  <Calendar size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                  {formatDate(purchaseOrder.orderDate)}
                </span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>{t('purchaseOrder.expectedDeliveryDate')}</span>
                <span className={purchaseOrder.expectedDeliveryDate ? styles.infoValue : styles.infoValueMuted}>
                  {purchaseOrder.expectedDeliveryDate ? (
                    <>
                      <Calendar size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                      {formatDate(purchaseOrder.expectedDeliveryDate)}
                    </>
                  ) : t('purchaseOrder.notSpecified')}
                </span>
              </div>
              {purchaseOrder.notes && (
                <div className={`${styles.infoItem} ${styles.fullWidth}`}>
                  <span className={styles.infoLabel}>{t('purchaseOrder.notes')}</span>
                  <span className={styles.infoValue}>{purchaseOrder.notes}</span>
                </div>
              )}
            </div>
          </div>

          {/* Supplier Information */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <User className={styles.cardIcon} size={20} />
              <h3>{t('purchaseOrder.supplierInformation')}</h3>
            </div>
            {purchaseOrder.Supplier ? (
              <div>
                <div className={styles.supplierName}>{purchaseOrder.Supplier.name}</div>
                {purchaseOrder.Supplier.contactPerson && (
                  <div className={styles.detailRow}>
                    <User size={14} />
                    {purchaseOrder.Supplier.contactPerson}
                  </div>
                )}
                {purchaseOrder.Supplier.email && (
                  <div className={styles.detailRow}>
                    <Mail size={14} />
                    {purchaseOrder.Supplier.email}
                  </div>
                )}
                {purchaseOrder.Supplier.phone && (
                  <div className={styles.detailRow}>
                    <Phone size={14} />
                    {purchaseOrder.Supplier.phone}
                  </div>
                )}
                {purchaseOrder.Supplier.address && (
                  <div className={styles.detailRow}>
                    <MapPin size={14} />
                    {purchaseOrder.Supplier.address}
                    {purchaseOrder.Supplier.city ? `, ${purchaseOrder.Supplier.city}` : ''}
                    {purchaseOrder.Supplier.country ? `, ${purchaseOrder.Supplier.country}` : ''}
                  </div>
                )}
              </div>
            ) : (
              <span className={styles.infoValueMuted}>{t('purchaseOrder.notSpecified')}</span>
            )}
          </div>

          {/* Warehouse Information */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <Building2 className={styles.cardIcon} size={20} />
              <h3>{t('purchaseOrder.warehouseInformation')}</h3>
            </div>
            {purchaseOrder.Warehouse ? (
              <div>
                <div className={styles.warehouseName}>{purchaseOrder.Warehouse.name}</div>
                {purchaseOrder.Warehouse.address && (
                  <div className={styles.detailRow}>
                    <MapPin size={14} />
                    {purchaseOrder.Warehouse.address}
                  </div>
                )}
                {purchaseOrder.Warehouse.phone && (
                  <div className={styles.detailRow}>
                    <Phone size={14} />
                    {purchaseOrder.Warehouse.phone}
                  </div>
                )}
              </div>
            ) : (
              <span className={styles.infoValueMuted}>{t('purchaseOrder.notSpecified')}</span>
            )}
          </div>

          {/* Items */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <ShoppingCart className={styles.cardIcon} size={20} />
              <h3>{t('purchaseOrder.itemsOrdered')}</h3>
            </div>
            <div className={styles.tableWrapper}>
              <table className={styles.itemsTable}>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>{t('purchaseOrder.itemNumber')}</th>
                    <th>{t('purchaseOrder.itemProduct')}</th>
                    <th className={styles.alignCenter}>{t('purchaseOrder.orderedQuantity')}</th>
                    <th className={styles.alignCenter}>{t('purchaseOrder.receivedQuantity')}</th>
                    <th className={styles.alignRight}>{t('purchaseOrder.itemUnitPrice')}</th>
                    <th className={styles.alignRight}>{t('purchaseOrder.itemLineTotal')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(purchaseOrder.items || []).map((item, index) => {
                    const variationLabel = getItemVariationLabel(item);
                    const receivedStatus = getReceivedStatus(item);
                    const sku = item.ProductVariation?.sku || item.Product?.sku;
                    return (
                      <tr key={item.id}>
                        <td className={styles.itemIndexCell}>{index + 1}</td>
                        <td>
                          <div className={styles.productCellInner}>
                            <ProductThumb item={item} alt={item.Product?.name || ''} />
                            <div className={styles.productCell}>
                              <span className={styles.productName}>{item.Product?.name || t('common.na')}</span>
                              {sku && <span className={styles.productSku}>SKU: {sku}</span>}
                              {variationLabel && (
                                <span className={styles.variationBadge}>{variationLabel}</span>
                              )}
                              {item.notes && (
                                <span className={styles.itemNotesText}>{item.notes}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className={styles.alignCenter}>{parseFloat(item.quantity)}</td>
                        <td className={styles.alignCenter}>
                          <div className={styles.receivedProgress}>
                            <span className={
                              receivedStatus === 'full' ? styles.receivedFull :
                              receivedStatus === 'partial' ? styles.receivedPartial :
                              styles.receivedNone
                            }>
                              {parseFloat(item.receivedQuantity)}
                            </span>
                          </div>
                        </td>
                        <td className={styles.alignRight}>{formatCurrency(item.unitPrice)}</td>
                        <td className={styles.alignRight}>{formatCurrency(item.lineTotal)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Related Documents (§9.5 step 6) */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <FileText className={styles.cardIcon} size={20} />
              <h3>{t('purchaseOrder.relatedDocuments')}</h3>
            </div>

            <div className={styles.relationsGrid}>
              {goodsReceivedNotes.length > 0 && (
                <div className={styles.relationsSection}>
                  <div className={styles.relationsSectionHeader}>
                    <PackagePlus size={18} />
                    <h3>{t('purchaseOrder.goodsReceivedNotes')}</h3>
                  </div>
                  <div className={styles.relationsList}>
                    {goodsReceivedNotes.map((grn) => (
                      <Link key={grn.id} to={`/procurement/grn/${grn.id}`} className={styles.relationLink}>
                        <span>{grn.grnNumber} — {grn.receivedAt ? formatDateTime(grn.receivedAt) : t('common.na')}</span>
                        <span className={styles.relationMeta}>
                          <Badge type={grn.status === 'cancelled' ? 'error' : 'success'} label={t(`goodsReceivedNote.status_${grn.status}`)} />
                          {grn.status !== 'cancelled' && canCancelGrn && (
                            <button
                              type="button"
                              className={styles.inlineCancelButton}
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCancelGrnTarget(grn); }}
                            >
                              {t('common.cancel')}
                            </button>
                          )}
                          <ExternalLink size={14} />
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {returnsToVendor.length > 0 && (
                <div className={styles.relationsSection}>
                  <div className={styles.relationsSectionHeader}>
                    <Undo2 size={18} />
                    <h3>{t('purchaseOrder.returnsToVendor')}</h3>
                  </div>
                  <div className={styles.relationsList}>
                    {returnsToVendor.map((rtv) => (
                      <Link key={rtv.id} to={`/procurement/return-to-vendor/${rtv.id}`} className={styles.relationLink}>
                        <span>{rtv.rtvNumber} - {formatCurrency(rtv.totalDebitAmount)}</span>
                        <span className={styles.relationMeta}>
                          <Badge type={rtv.status === 'voided' ? 'error' : 'success'} label={t(`returnToVendor.status_${rtv.status}`)} />
                          <ExternalLink size={14} />
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {debitNotes.length > 0 && (
                <div className={styles.relationsSection}>
                  <div className={styles.relationsSectionHeader}>
                    <FileText size={18} />
                    <h3>{t('purchaseOrder.debitNotes')}</h3>
                  </div>
                  <div className={styles.relationsList}>
                    {debitNotes.map((dn) => (
                      <Link key={dn.id} to={`/procurement/debit-notes/${dn.id}`} className={styles.relationLink}>
                        <span>{dn.debitNoteNumber} ({formatCurrency(dn.appliedAmount)} / {formatCurrency(dn.amount)})</span>
                        <span className={styles.relationMeta}>
                          <Badge type={dn.status === 'cancelled' ? 'error' : dn.status === 'applied' ? 'success' : 'info'} label={t(`debitNote.status_${dn.status}`)} />
                          <ExternalLink size={14} />
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

            </div>

            {(goodsReceivedNotes.length === 0 && returnsToVendor.length === 0 && debitNotes.length === 0) && (
              <p className={styles.infoValueMuted}>{t('purchaseOrder.noRelatedDocuments')}</p>
            )}
          </div>

          {/* Audit History (procurement audit trail — mirrors OrderDetailsPage) */}
          <div className={styles.card}>
            <div className={`${styles.cardHeader} ${styles.cardHeaderWithAction}`}>
              <div className={styles.cardHeaderTitle}>
                <History className={styles.cardIcon} size={20} />
                <h3>{t('purchaseOrder.auditHistory')}</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowAuditHistory(!showAuditHistory)}
                className={styles.toggleButton}
                aria-expanded={showAuditHistory}
              >
                {showAuditHistory ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                {showAuditHistory ? t('common.hide') : t('common.show')}
              </button>
            </div>

            {auditSummary && (
              <div className={styles.auditSummaryBar}>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>{t('purchaseOrder.totalChanges')}</span>
                  <span className={styles.summaryValue}>{auditSummary.totalChanges || 0}</span>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>{t('purchaseOrder.lastModified')}</span>
                  <span className={styles.summaryValue}>
                    {auditSummary.lastModified ? formatDateTime(auditSummary.lastModified) : t('common.na')}
                  </span>
                </div>
              </div>
            )}

            {showAuditHistory && (
              isLoadingAudit ? (
                <div className={styles.auditLoading}>
                  <Loader2 size={16} />
                  <span>{t('common.loading')}</span>
                </div>
              ) : auditHistory.length > 0 ? (
                <>
                  <div className={styles.auditList}>
                    {auditHistory.map((audit) => {
                      const color = getAuditActionColor(audit.action);
                      const tagKey = AUDIT_ENTITY_TAG_KEYS[audit.entityType];
                      const routeFor = AUDIT_ENTITY_ROUTES[audit.entityType];
                      const docNumber = audit.changes?.grnNumber || audit.changes?.rtvNumber || audit.changes?.debitNoteNumber
                        || audit.changes?.paymentRecord?.to?.debitNoteNumber;
                      return (
                        <div key={audit.id} className={styles.auditItem}>
                          <div className={styles.auditHeader}>
                            <div className={styles.auditAction}>
                              <div
                                className={styles.auditActionBadge}
                                style={{ backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)`, color }}
                              >
                                {getAuditActionIcon(audit.action)}
                                <span>{t(`purchaseOrder.audit_${audit.action}`)}</span>
                              </div>
                              {tagKey && (
                                routeFor && audit.entityId ? (
                                  <Link to={routeFor(audit.entityId)} className={styles.auditPaymentRecordTag}>
                                    {t(tagKey)}{docNumber ? ` · ${docNumber}` : ''}
                                  </Link>
                                ) : (
                                  <span className={styles.auditPaymentRecordTag}>{t(tagKey)}</span>
                                )
                              )}
                              <span className={styles.auditHistoryUser}>
                                {t('purchaseOrder.auditBy', { name: audit.changedBy?.name || t('common.na') })}
                              </span>
                            </div>
                            <div className={styles.auditHistoryDate}>
                              <Clock size={12} />
                              {formatDateTime(audit.createdAt)}
                            </div>
                          </div>

                          {audit.changes && Object.keys(audit.changes).length > 0 && renderAuditChanges(audit.changes)}

                          {audit.changeReason && (
                            <div className={styles.auditReason}>
                              <FileText size={12} />
                              {audit.changeReason}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {auditTotal > AUDIT_LIMIT && (
                    <div className={styles.pagination}>
                      <button
                        type="button"
                        onClick={() => loadAuditHistory(auditPage - 1)}
                        disabled={auditPage === 1}
                        className={styles.paginationButton}
                      >
                        {t('purchaseOrder.previous')}
                      </button>
                      <span className={styles.paginationInfo}>
                        {t('purchaseOrder.pageOf', { page: auditPage, total: Math.ceil(auditTotal / AUDIT_LIMIT) })}
                      </span>
                      <button
                        type="button"
                        onClick={() => loadAuditHistory(auditPage + 1)}
                        disabled={auditPage >= Math.ceil(auditTotal / AUDIT_LIMIT)}
                        className={styles.paginationButton}
                      >
                        {t('purchaseOrder.next')}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.emptyState}>
                  <History size={32} />
                  <p>{t('purchaseOrder.noActivity')}</p>
                </div>
              )
            )}
          </div>
        </div>

        {/* Right Sidebar */}
        <div className={styles.rightSidebar}>
          {/* Financial Summary */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <DollarSign className={styles.cardIcon} size={20} />
              <h3>{t('purchaseOrder.financialSummary')}</h3>
            </div>
            <div className={styles.financialRows}>
              <div className={styles.financialRow}>
                <span className={styles.financialLabel}>{t('purchaseOrder.subtotal')}</span>
                <span className={styles.financialValue}>{formatCurrency(purchaseOrder.totalAmount)}</span>
              </div>

              {parseFloat(purchaseOrder.extraCharges) > 0 && (
                <div className={styles.financialRow}>
                  <div>
                    <div className={styles.financialLabel}>{t('purchaseOrder.extraCharges')}</div>
                    {purchaseOrder.extraChargesDescription && (
                      <div className={styles.financialDesc}>{purchaseOrder.extraChargesDescription}</div>
                    )}
                  </div>
                  <span className={`${styles.financialValue} ${styles.financialValuePositive}`}>
                    +{formatCurrency(purchaseOrder.extraCharges)}
                  </span>
                </div>
              )}

              {parseFloat(purchaseOrder.discountAmount) > 0 && (
                <div className={styles.financialRow}>
                  <div>
                    <div className={styles.financialLabel}>{t('purchaseOrder.discountAmount')}</div>
                    {purchaseOrder.discountDescription && (
                      <div className={styles.financialDesc}>{purchaseOrder.discountDescription}</div>
                    )}
                  </div>
                  <span className={`${styles.financialValue} ${styles.financialValueNegative}`}>
                    -{formatCurrency(purchaseOrder.discountAmount)}
                  </span>
                </div>
              )}

              <div className={styles.grandTotalRow}>
                <span className={styles.grandTotalLabel}>{t('purchaseOrder.grandTotal')}</span>
                <span className={styles.grandTotalValue}>{formatCurrency(purchaseOrder.grandTotal)}</span>
              </div>
            </div>
          </div>

          {/* Payment Records */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <CreditCard className={styles.cardIcon} size={20} />
              <h3>{t('purchaseOrder.paymentRecords')}</h3>
            </div>
            {payments.length > 0 ? (
              <PaymentHistoryList
                payments={payments.map((p) => ({
                  id: p.id,
                  iconFileName: p.paymentMethod?.iconFileName,
                  label: p.paymentMethod?.label,
                  amount: p.paymentAmount,
                  reference: p.reference,
                  date: p.paymentDate,
                  referenceTo: p.debitNote?.id ? `/procurement/debit-notes/${p.debitNote.id}` : null,
                }))}
              />
            ) : (
              <p className={styles.infoValueMuted}>{t('purchaseOrder.noPaymentRecords')}</p>
            )}
            <div className={styles.paymentSummaryLine}>
              <span>{t('purchaseOrder.paymentAmount')}: <strong>{formatCurrency(purchaseOrder.paidAmount)}</strong></span>
              <span>{t('purchaseOrder.balanceAmount')}: <strong>{formatCurrency(purchaseOrder.balanceAmount)}</strong></span>
              <Badge
                type={purchaseOrder.paymentStatus === 'paid' ? 'success' : purchaseOrder.paymentStatus === 'partial' ? 'warning' : 'error'}
                label={t(`purchaseOrder.paymentStatus_${purchaseOrder.paymentStatus || 'unpaid'}`)}
              />
            </div>
          </div>

          {/* Audit Information */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <Truck className={styles.cardIcon} size={20} />
              <h3>{t('purchaseOrder.auditInformation')}</h3>
            </div>
            <div className={styles.auditRows}>
              <div className={styles.auditRow}>
                <span className={styles.auditLabel}>{t('common.createdBy')}</span>
                <span className={styles.auditUser}>{purchaseOrder.createdBy?.name || t('common.na')}</span>
                <span className={styles.auditDate}>{formatDateTime(purchaseOrder.createdAt)}</span>
              </div>

              {purchaseOrder.receivedBy && (
                <>
                  <hr className={styles.auditDivider} />
                  <div className={styles.auditRow}>
                    <span className={styles.auditLabel}>{t('purchaseOrder.receivedBy')}</span>
                    <span className={styles.auditUser}>{purchaseOrder.receivedBy.name}</span>
                    {purchaseOrder.receivedDate && (
                      <span className={styles.auditDate}>{formatDate(purchaseOrder.receivedDate)}</span>
                    )}
                  </div>
                </>
              )}

              {purchaseOrder.cancelledBy && (
                <>
                  <hr className={styles.auditDivider} />
                  <div className={styles.auditRow}>
                    <span className={styles.auditLabel}>{t('purchaseOrder.cancelledBy')}</span>
                    <span className={styles.auditUser}>{purchaseOrder.cancelledBy.name}</span>
                    {purchaseOrder.cancelledAt && (
                      <span className={styles.auditDate}>{formatDateTime(purchaseOrder.cancelledAt)}</span>
                    )}
                    <span className={styles.auditLabel} style={{ marginTop: '0.5rem' }}>
                      {t('purchaseOrder.cancellationReason')}
                    </span>
                    <span className={purchaseOrder.cancellationReason ? styles.auditUser : styles.infoValueMuted}>
                      {purchaseOrder.cancellationReason || t('purchaseOrder.noCancellationReason')}
                    </span>
                  </div>
                </>
              )}
              {returnsToVendor.map((rtv) => (
                <React.Fragment key={rtv.id}>
                  <hr className={styles.auditDivider} />
                  <div className={styles.auditRow}>
                    <span className={styles.auditLabel}>
                      {t('purchaseOrder.returnedToVendor')}{' '}
                      <Link to={AUDIT_ENTITY_ROUTES.rtv(rtv.id)} className={styles.auditDocLink}>{rtv.rtvNumber}</Link>
                    </span>
                    <span className={styles.auditUser}>{rtv.createdBy?.name || t('common.na')}</span>
                    <span className={styles.auditDate}>{formatDateTime(rtv.createdAt)}</span>
                    {rtv.status === 'voided' && (
                      <Badge type="error" label={t('purchaseOrder.voided')} />
                    )}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Receive Dialog */}
      {showReceiveDialog && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialogContent}>
            <div className={styles.dialogHeader}>
              <CheckCircle className={styles.dialogIconSuccess} size={24} />
              <h3>{t('purchaseOrder.confirmReceiveTitle')}</h3>
            </div>
            <div className={styles.dialogBody}>
              <p>{t('purchaseOrder.confirmReceive')}</p>
            </div>
            <div className={styles.dialogActions}>
              <button
                onClick={() => setShowReceiveDialog(false)}
                className={styles.dialogCancelButton}
                disabled={isReceiving}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleReceive}
                className={styles.dialogReceiveButton}
                disabled={isReceiving}
              >
                {isReceiving ? t('purchaseOrder.receivingOrder') : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel GRN Dialog (replaces the old whole-order Reverse action) */}
      {cancelGrnTarget && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialogContent}>
            <div className={styles.dialogHeader}>
              <XCircle className={styles.dialogIcon} size={24} />
              <h3>{t('goodsReceivedNote.cancelGoodsReceivedNote')}</h3>
            </div>
            <div className={styles.dialogBody}>
              <p>{t('goodsReceivedNote.confirmCancel')}</p>
              <p><strong>{cancelGrnTarget.grnNumber}</strong></p>
              <label className={styles.dialogLabel}>
                {t('goodsReceivedNote.cancellationReason')}
              </label>
              <textarea
                className={styles.dialogTextarea}
                value={grnCancellationReason}
                onChange={(e) => setGrnCancellationReason(e.target.value)}
                placeholder={t('goodsReceivedNote.cancellationReasonPlaceholder')}
                disabled={isCancellingGrn}
              />
            </div>
            <div className={styles.dialogActions}>
              <button
                onClick={() => { setCancelGrnTarget(null); setGrnCancellationReason(''); }}
                className={styles.dialogCancelButton}
                disabled={isCancellingGrn}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCancelGrn}
                className={styles.dialogConfirmButton}
                disabled={isCancellingGrn}
              >
                {isCancellingGrn ? t('common.loading') : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPrint && (
        <PrintPreviewModal
          title={`${t('purchaseOrder.purchaseOrderDetails')} — ${purchaseOrder.purchaseOrderNumber}`}
          onClose={() => setShowPrint(false)}
          pdfFileName={`Purchase_Order_${purchaseOrder.purchaseOrderNumber || purchaseOrder.id}.pdf`}
          onDownloadPdf={() => exportPurchaseOrderPDF(purchaseOrder, {
            primary: getCssVar('--color-primary', '#4f46e5'),
            primaryDark: getCssVar('--color-primary-dark', '#3730a3'),
          })}
          renderA4={() => <PurchaseOrderA4Document purchaseOrder={purchaseOrder} />}
        />
      )}

      {/* Cancel Dialog */}
      {showCancelDialog && (
        <div className={styles.dialogOverlay}>
          <div className={styles.dialogContent}>
            <div className={styles.dialogHeader}>
              <AlertCircle className={styles.dialogIcon} size={24} />
              <h3>{t('purchaseOrder.confirmCancelTitle')}</h3>
            </div>
            <div className={styles.dialogBody}>
              <p>{t('purchaseOrder.confirmCancel')}</p>
              <label className={styles.dialogLabel}>
                {t('purchaseOrder.cancellationReasonLabel')}
              </label>
              <textarea
                className={styles.dialogTextarea}
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder={t('purchaseOrder.cancellationReasonPlaceholder')}
                disabled={isCancelling}
              />
            </div>
            <div className={styles.dialogActions}>
              <button
                onClick={() => { setShowCancelDialog(false); setCancellationReason(''); }}
                className={styles.dialogCancelButton}
                disabled={isCancelling}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCancel}
                className={styles.dialogConfirmButton}
                disabled={isCancelling}
              >
                {isCancelling ? t('purchaseOrder.cancellingOrder') : t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseOrderDetailsPage;
